import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const values = [
  { icon: '🌱', title: 'Sustainability', desc: 'Every action rooted in long-term ecological balance and harmony with nature.' },
  { icon: '🤝', title: 'Community', desc: 'Building bonds between people, communities, and the environment they share.' },
  { icon: '🌍', title: 'Global Vision', desc: 'Local actions, global impact — escalating every initiative to worldwide reach.' },
  { icon: '❤️', title: 'Love & Memory', desc: 'Turning human connections into living, breathing tributes to our loved ones.' },
  { icon: '🏛️', title: 'Governance', desc: 'Transparent operations in full coordination with Forest Departments and authorities.' },
  { icon: '🌾', title: 'Innovation', desc: 'Agri-tech startup arm empowering farmers with sustainable, market-ready solutions.' },
];

const team = [
  { name: 'Y2K Founder', role: 'Visionary & Founder', emoji: '👨‍🌾' },
  { name: 'Forest Dept.', role: 'Government Partner', emoji: '🏛️' },
  { name: 'CallTree Team', role: 'Technology & Ops', emoji: '💻' },
  { name: 'Volunteers', role: 'Field Champions', emoji: '🌿' },
];

export default function About() {
  return (
    <div className="about">
      {/* PAGE HERO */}
      <section className="page-hero about-hero">
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <span className="tag">About Us</span>
          <h1 className="page-hero__title">We Are Y2K Global Nexus</h1>
          <p className="page-hero__sub">
            An NGO born from love for the environment — driving tree planting, climate awareness,
            community clean drives, and sustainable agriculture across India and beyond.
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="about-who section-padding">
        <div className="container">
          <div className="about-who__grid">
            <FadeSection>
              <div className="about-who__content">
                <span className="tag">Who We Are</span>
                <h2 className="section-title">Environment Protection,<br />One Tree at a Time</h2>
                <p className="section-subtitle" style={{ marginBottom: '20px' }}>
                  Y2K Global Nexus is a registered NGO dedicated to environmental protection and climate action.
                  We believe that every tree planted is a memory — a testament to human love, responsibility, and hope.
                </p>
                <p style={{ color: 'var(--text-medium)', lineHeight: '1.8', marginBottom: '32px' }}>
                  Founded with a singular vision: to connect people emotionally with nature through meaningful
                  acts like gifting trees, organizing clean drives, and spreading environmental awareness.
                  We coordinate directly with Forest Departments to ensure every sapling planted counts.
                </p>
                <div className="about-who__highlights">
                  <div className="about-highlight">
                    <span className="about-highlight__icon">🌲</span>
                    <div>
                      <strong>Tree Planting</strong>
                      <span>Coordinated with Forest Dept.</span>
                    </div>
                  </div>
                  <div className="about-highlight">
                    <span className="about-highlight__icon">🌍</span>
                    <div>
                      <strong>Global Reach</strong>
                      <span>Escalating to worldwide level</span>
                    </div>
                  </div>
                  <div className="about-highlight">
                    <span className="about-highlight__icon">🌾</span>
                    <div>
                      <strong>Agri Startup</strong>
                      <span>Sustainable agri products</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeSection>
            <FadeSection delay={200}>
              <div className="about-who__visual">
                <div className="about-visual-main">
                  <div className="about-visual-main__icon">🌍</div>
                  <h3>Y2K Global Nexus</h3>
                  <p>NGO for Environment Protection</p>
                  <div className="about-visual-main__badges">
                    <span>🏛️ Forest Dept. Partner</span>
                    <span>📋 NGO Registered</span>
                    <span>🌱 calltree.in</span>
                  </div>
                </div>
                <div className="about-visual-orbs">
                  <div className="about-orb about-orb--1">🌳 Tree Planting</div>
                  <div className="about-orb about-orb--2">🧹 Clean Drives</div>
                  <div className="about-orb about-orb--3">🌾 Agri Products</div>
                  <div className="about-orb about-orb--4">📢 Awareness</div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="about-values section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div className="section-header" style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span className="tag">Our Values</span>
              <h2 className="section-title">What Drives Us</h2>
            </div>
          </FadeSection>
          <div className="values-grid">
            {values.map(({ icon, title, desc }, i) => (
              <FadeSection key={i} delay={i * 80}>
                <div className="value-card">
                  <div className="value-card__icon">{icon}</div>
                  <h3 className="value-card__title">{title}</h3>
                  <p className="value-card__desc">{desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* CALLTREE PROJECT */}
      <section className="about-project section-padding">
        <div className="container">
          <FadeSection>
            <div className="about-project__card">
              <div className="about-project__content">
                <span className="tag">Featured Initiative</span>
                <h2 className="section-title">The CallTree Project</h2>
                <p className="section-subtitle" style={{ marginBottom: '24px' }}>
                  calltree.in is the digital platform for all our activities — tree planting drives,
                  registration portals, event coordination with the Forest Department, and global environmental campaigns.
                </p>
                <p style={{ color: 'var(--text-medium)', lineHeight: '1.8', marginBottom: '32px' }}>
                  The <strong>"Plant a Love"</strong> campaign — launched on Valentine's Day — invites people
                  to gift a tree to someone they love. A living memory, green and ever-lasting. This activity
                  is coordinated in full partnership with the Forest Department and is being scaled globally by Y2K.
                </p>
                <Link to="/calltree" className="btn-primary">Explore CallTree →</Link>
              </div>
              <div className="about-project__stats">
                {[
                  { n: '🌱', v: 'Plant a Love', d: 'Valentine\'s Campaign' },
                  { n: '🏛️', v: 'Forest Dept.', d: 'Official Partner' },
                  { n: '🌍', v: 'Global Scale', d: 'Y2K leads expansion' },
                ].map(({ n, v, d }, i) => (
                  <div key={i} className="project-stat">
                    <span className="project-stat__icon">{n}</span>
                    <strong>{v}</strong>
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* TEAM */}
      <section className="about-team section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span className="tag">Our Network</span>
              <h2 className="section-title">Powered by People</h2>
            </div>
          </FadeSection>
          <div className="team-grid">
            {team.map(({ name, role, emoji }, i) => (
              <FadeSection key={i} delay={i * 100}>
                <div className="team-card">
                  <div className="team-card__avatar">{emoji}</div>
                  <h3 className="team-card__name">{name}</h3>
                  <p className="team-card__role">{role}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container">
          <FadeSection>
            <div className="about-cta__inner">
              <h2>Be Part of the <span className="gold-text">Y2K Movement</span></h2>
              <p>Join us in building a greener, healthier, more connected world. Every tree counts.</p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-gold">🌱 Join as Volunteer</Link>
                <Link to="/activities" className="btn-outline" style={{ borderColor: 'white', color: 'white' }}>
                  See Activities
                </Link>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
